import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { confirmStripeCheckoutSession } from '../../services/api';
import {
  AlertCircle,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Grid3X3,
  Infinity as InfinityIcon,
  Layers3,
  Loader2,
  PackageOpen,
  Plus,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  XCircle,
} from 'lucide-react';
import './SubscriptionsManager.scss';

const STATUS_MAP = {
  active: { labelKey: 'status.active', fallback: 'Activa', tone: 'success' },
  canceled: { labelKey: 'status.canceled', fallback: 'Cancelada', tone: 'danger' },
  expired: { labelKey: 'status.expired', fallback: 'Expirada', tone: 'warning' },
  past_due: { labelKey: 'status.past_due', fallback: 'Pago pendiente', tone: 'warning' },
  trialing: { labelKey: 'status.trialing', fallback: 'En prueba', tone: 'success' },
};

const BILLING_PERIODS = {
  monthly: { labelKey: 'payment.billing.monthly', periodKey: 'payment.billing.monthly_period' },
  semiannual: { labelKey: 'payment.billing.semiannual', periodKey: 'payment.billing.semiannual_period' },
  annual: { labelKey: 'payment.billing.annual', periodKey: 'payment.billing.annual_period' },
};

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

const formatDate = (str, fallback = 'Sin fecha') => {
  if (!str) return fallback;
  return new Date(str).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatMoney = (amount) => {
  const value = Number(amount || 0);
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  });
};

const getPlanDescription = (name = '', translate = (_, fallback) => fallback) => {
  if (name.includes('Ilimitado')) return translate('plan.unlimited_desc', 'Collabs ilimitados con creators ilimitados');
  const match = name.match(/(\d+) Collab/);
  if (match) {
    const count = Number(match[1]);
    return translate('plan.count_desc', '{{count}} collab activa', { count });
  }
  return translate('plan.custom_desc', 'Plan personalizado');
};

const getRemainingCollabs = (subscription) => {
  if (Number(subscription.max_collabs) === 0) return Infinity;
  return Math.max(Number(subscription.max_collabs || 0) - Number(subscription.used_collabs || 0), 0);
};

const getUsagePercent = (subscription) => {
  if (Number(subscription.max_collabs) === 0) return 100;
  if (!Number(subscription.max_collabs)) return 0;
  return Math.min(100, Math.round((Number(subscription.used_collabs || 0) / Number(subscription.max_collabs)) * 100));
};

const SubscriptionsManager = () => {
  const { t } = useTranslation('business');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCancelation, setActiveCancelation] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const st = useCallback((key, fallback, options = {}) => (
    t(`subscriptions.${key}`, { defaultValue: fallback, ...options })
  ), [t]);

  const fetchSubscriptions = useCallback(async (retryCount = 0) => {
    let scheduledRetry = false;

    try {
      setLoading(true);
      setError('');
      setRetrying(false);

      const res = await axios.get('/subscriptions/', {
        });

      const data = Array.isArray(res.data)
        ? res.data
        : res.data.results ?? [];

      setSubscriptions(data);
    } catch (err) {
      if ((err.code === 'ECONNABORTED' || !err.response) && retryCount < 3) {
        scheduledRetry = true;
        setRetrying(true);
        window.setTimeout(() => fetchSubscriptions(retryCount + 1), Math.pow(2, retryCount) * 1000);
        return;
      }
      setError('No se pudieron cargar tus suscripciones. Por favor, intentalo de nuevo.');
      setSubscriptions([]);
    } finally {
      if (!scheduledRetry) {
        setLoading(false);
        setRetrying(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchSubscriptions();
    else setLoading(false);
  }, [isAuthenticated, fetchSubscriptions]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!isAuthenticated || params.get('stripe') !== 'success' || !sessionId) return undefined;

    let cancelled = false;
    const confirmCheckout = async () => {
      try {
        await confirmStripeCheckoutSession(sessionId);
        if (!cancelled) {
          await fetchSubscriptions();
          navigate(location.pathname, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'No se pudo confirmar tu suscripcion con Stripe.');
        }
      }
    };

    confirmCheckout();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, fetchSubscriptions, location.pathname, location.search, navigate]);

  const validSubscriptions = useMemo(
    () => subscriptions.filter((subscription) => subscription?.plan),
    [subscriptions]
  );

  const activeSubscriptions = useMemo(
    () => validSubscriptions.filter((subscription) => ACTIVE_STATUSES.has(subscription.status)),
    [validSubscriptions]
  );

  const primarySubscription = activeSubscriptions[0] || validSubscriptions[0];

  const totals = useMemo(() => {
    const active = activeSubscriptions.length;
    const finiteCollabs = activeSubscriptions.filter((subscription) => Number(subscription.max_collabs) !== 0);
    const unlimited = activeSubscriptions.some((subscription) => Number(subscription.max_collabs) === 0);
    const used = finiteCollabs.reduce((sum, subscription) => sum + Number(subscription.used_collabs || 0), 0);
    const max = finiteCollabs.reduce((sum, subscription) => sum + Number(subscription.max_collabs || 0), 0);

    return {
      active,
      used,
      max,
      remaining: unlimited ? Infinity : Math.max(max - used, 0),
      usagePercent: unlimited || !max ? 0 : Math.min(100, Math.round((used / max) * 100)),
      hasUnlimited: unlimited,
    };
  }, [activeSubscriptions]);

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      setProcessingAction(true);
      const response = await axios.post(`/subscriptions/${subscriptionId}/cancel/`, {}, {
        });
      setSubscriptions(prev =>
        prev.map(subscription => (
          subscription.id === subscriptionId
            ? {
                ...subscription,
                status: response.data.status,
                cancel_at_period_end: response.data.cancel_at_period_end,
                end_date: response.data.access_until || subscription.end_date,
              }
            : subscription
        ))
      );
      setActiveCancelation(null);
    } catch {
      setError('Hubo un error al cancelar la suscripcion. Por favor, intentalo de nuevo.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRetry = () => {
    setRetrying(true);
    fetchSubscriptions();
  };

  const renderTopBar = () => (
    <section className="subscriptions-hero">
      <div className="hero-copy">
        <div className="eyebrow">
          <Sparkles size={14} />
          {st('hero.eyebrow', 'Billing atelier')}
        </div>
        <h1>{st('hero.title', 'Subscription command center')}</h1>
        <p>
          {st('hero.subtitle', 'Controla tus slots de canje, facturacion y proximo movimiento de collabs desde una vista mas clara y accionable.')}
        </p>
      </div>

      <div className="hero-actions">
        <Link to="/business/plans" className="quiet-link">
          <Grid3X3 size={16} />
          {st('actions.plans', 'Planes')}
        </Link>
        <Link to="/business/transactions" className="quiet-link">
          <ReceiptText size={16} />
          {st('actions.payments', 'Pagos')}
        </Link>
      </div>
    </section>
  );

  if (loading) {
    return (
      <div className="subscriptions-page">
        {renderTopBar()}
        <section className="subscription-loading">
          <Loader2 className="spinning" size={26} />
          <h2>{retrying ? st('loading.retrying', 'Reintentando conexion') : st('loading.title', 'Preparando tu workspace')}</h2>
          <p>{st('loading.body', 'Estamos sincronizando tus planes y cupos disponibles.')}</p>
          <button type="button" onClick={handleRetry}>
            <RefreshCw size={15} />
            {st('actions.retry', 'Reintentar')}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="subscriptions-page">
      {renderTopBar()}

      {location.state?.message && (
        <div className="subscription-banner success">
          <CheckCircle2 size={18} />
          {location.state.message}
        </div>
      )}

      {error && (
        <div className="subscription-banner danger">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button type="button" onClick={handleRetry}>
            {st('actions.retry', 'Reintentar')}
          </button>
        </div>
      )}

      {validSubscriptions.length === 0 ? (
        <section className="subscription-empty">
          <div className="empty-icon">
            <PackageOpen size={34} />
          </div>
          <div>
            <p className="eyebrow">{st('empty.eyebrow', 'Sin plan activo')}</p>
            <h2>{st('empty.title', 'Elige un plan para publicar collabs')}</h2>
            <p>
              {st('empty.body', 'Necesitas una suscripcion para activar ofertas de colaboracion y administrar invitaciones de creadores.')}
            </p>
          </div>
          <Link to="/business/plans" className="primary-cta">
            <CreditCard size={18} />
            {st('empty.cta', 'Ver planes disponibles')}
          </Link>
        </section>
      ) : (
        <>
          <section className="subscription-overview">
            <div className="overview-card feature">
              <span>{st('overview.primary_plan', 'Plan principal')}</span>
              <strong>{primarySubscription?.plan?.name || st('overview.active_plan', 'Plan activo')}</strong>
              <p>{primarySubscription ? getPlanDescription(primarySubscription.plan.name, st) : st('overview.collab_management', 'Gestion de collabs')}</p>
            </div>
            <div className="overview-card">
              <span>{st('overview.active_subscriptions', 'Suscripciones activas')}</span>
              <strong>{totals.active}</strong>
              <p>{st('overview.total_registered', '{{count}} total registradas', { count: validSubscriptions.length })}</p>
            </div>
            <div className="overview-card">
              <span>{st('overview.available_slots', 'Slots disponibles')}</span>
              <strong>{totals.hasUnlimited ? st('common.unlimited', 'Ilimitados') : totals.remaining}</strong>
              <p>{totals.hasUnlimited ? st('overview.unlimited_exchange', 'Sin limite de canje') : st('overview.usage_summary', '{{used}} de {{max}} en uso', { used: totals.used, max: totals.max })}</p>
            </div>
          </section>

          <div className="subscriptions-layout">
            <section className="plans-stack">
              {validSubscriptions.map(subscription => {
                const statusMeta = STATUS_MAP[subscription.status] ?? { labelKey: 'status.unknown', fallback: 'Desconocido', tone: 'neutral' };
                const periodMeta = BILLING_PERIODS[subscription.plan.billing_period] || BILLING_PERIODS.monthly;
                const billingMeta = {
                  label: t(periodMeta.labelKey),
                  period: t(periodMeta.periodKey),
                };
                const isUnlimited = Number(subscription.max_collabs) === 0;
                const usagePercent = getUsagePercent(subscription);
                const remainingCollabs = getRemainingCollabs(subscription);
                // An active subscription allows paid collabs even when all
                // exchange slots are occupied. Publication enforces the type.
                const canCreateCollab = ACTIVE_STATUSES.has(subscription.status);

                return (
                  <article key={subscription.id} className="subscription-panel">
                    <div className="panel-top">
                      <div>
                        <span className={`status-pill ${statusMeta.tone}`}>
                          <span />
                          {st(statusMeta.labelKey, statusMeta.fallback)}
                        </span>
                        <h2>{subscription.plan.name || st('plan.unnamed', 'Plan sin nombre')}</h2>
                        <p>{getPlanDescription(subscription.plan.name, st)}</p>
                      </div>
                      <div className="price-block">
                        <strong>{formatMoney(subscription.plan.price)}</strong>
                        <span>{billingMeta.period}</span>
                      </div>
                    </div>

                    <div className="panel-grid">
                      <div className="usage-module">
                        <div className="usage-ring" style={{ '--usage': `${usagePercent}%` }}>
                          <div>
                            {isUnlimited ? <InfinityIcon size={30} /> : <strong>{usagePercent}%</strong>}
                            <span>{isUnlimited ? st('usage.no_limit', 'sin limite') : st('usage.used', 'usado')}</span>
                          </div>
                        </div>
                        <div className="usage-copy">
                          <h3>{isUnlimited ? st('usage.unlimited_title', 'Collabs ilimitados') : st('usage.title', 'Uso de collabs')}</h3>
                          <p>
                            {isUnlimited
                              ? st('usage.unlimited_body', 'Puedes publicar collabs activas sin consumir cupos.')
                              : st('usage.body', '{{used}} de {{max}} collabs de canje estan activas.', { used: subscription.used_collabs || 0, max: subscription.max_collabs })}
                          </p>
                          <div className="meter-track">
                            <div style={{ width: `${usagePercent}%` }} />
                          </div>
                          <small>
                            {isUnlimited
                               ? st('usage.unlimited_note', 'Tu plan no tiene límite de collabs activas.')
                               : st('usage.remaining_note', '{{count}} slots disponibles en tu plan.', { count: remainingCollabs })}
                          </small>
                        </div>
                      </div>

                      <div className="facts-grid">
                        <div className="fact">
                          <CalendarDays size={17} />
                          <span>{st('facts.start', 'Inicio')}</span>
                          <strong>{formatDate(subscription.start_date, st('common.no_date', 'Sin fecha'))}</strong>
                        </div>
                        <div className="fact">
                          <CalendarDays size={17} />
                          <span>{st('facts.renewal', 'Renovacion')}</span>
                          <strong>{subscription.end_date ? formatDate(subscription.end_date, st('common.no_date', 'Sin fecha')) : st('facts.continuous', 'Continua')}</strong>
                        </div>
                        <div className="fact">
                          <CreditCard size={17} />
                          <span>{st('facts.billing', 'Facturacion')}</span>
                          <strong>{billingMeta.label}</strong>
                        </div>
                        <div className="fact">
                          <Users size={17} />
                          <span>{st('facts.creators_per_collab', 'Creators/collab')}</span>
                          <strong>
                            {Number(subscription.max_creators_per_collab) === 0
                              ? st('common.unlimited_plural', 'Ilimitados')
                              : st('facts.up_to', 'Hasta {{count}}', { count: subscription.max_creators_per_collab })}
                          </strong>
                        </div>
                      </div>
                    </div>

                    <div className="panel-actions">
                      {subscription.cancel_at_period_end && (
                        <span className="subscription-cancellation-scheduled">
                          {st('cancel.scheduled', 'Cancelacion programada al finalizar el periodo pagado')}
                        </span>
                      )}
                      {ACTIVE_STATUSES.has(subscription.status) && subscription.plan.billing_period && !subscription.cancel_at_period_end && (
                        activeCancelation === subscription.id ? (
                          <div className="cancel-dock">
                            <span>{st('cancel.confirm', 'Confirmar cancelacion de este plan')}</span>
                            <button
                              type="button"
                              className="danger-action"
                              disabled={processingAction}
                              onClick={() => handleCancelSubscription(subscription.id)}
                            >
                              {processingAction ? <Loader2 className="spinning" size={16} /> : <XCircle size={16} />}
                              {st('cancel.confirm_cta', 'Si, cancelar')}
                            </button>
                            <button
                              type="button"
                              className="ghost-action"
                              disabled={processingAction}
                              onClick={() => setActiveCancelation(null)}
                            >
                              {st('cancel.keep', 'Mantener')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="ghost-action danger-text"
                            onClick={() => setActiveCancelation(subscription.id)}
                          >
                            <XCircle size={16} />
                            {st('cancel.open', 'Cancelar suscripcion')}
                          </button>
                        )
                      )}

                      {ACTIVE_STATUSES.has(subscription.status) && (
                        <button
                          type="button"
                          className="primary-cta"
                          disabled={!canCreateCollab}
                          onClick={() => navigate('/business/create-collab')}
                        >
                          <Plus size={18} />
                          {st('actions.new_collab', 'Nuevo Collab')}
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>

            <aside className="subscription-aside">
              <div className="aside-card">
                <div className="aside-icon">
                  <ShieldCheck size={22} />
                </div>
                <h3>{st('aside.status_title', 'Estado operativo')}</h3>
                <p>
                  {totals.active > 0
                    ? st('aside.status_ready', 'Tu cuenta esta lista para lanzar y administrar collabs.')
                    : st('aside.status_blocked', 'No hay una suscripcion activa para publicar nuevos collabs.')}
                </p>
                <div className="aside-progress">
                  <span>{st('aside.overall_usage', 'Uso general')}</span>
                  <strong>{totals.hasUnlimited ? st('common.unlimited', 'Ilimitado') : `${totals.usagePercent}%`}</strong>
                </div>
              </div>

              <div className="aside-card action-card">
                <Layers3 size={22} />
                <h3>{st('aside.plans_title', 'Explora planes')}</h3>
                <p>{st('aside.plans_body', 'Compara cupos, duracion y limites antes de tu proxima campana.')}</p>
                <Link to="/business/plans">
                  {st('actions.view_all_plans', 'Ver todos los planes')}
                  <ArrowUpRight size={15} />
                </Link>
              </div>

              <div className="aside-card action-card">
                <ReceiptText size={22} />
                <h3>{st('aside.payments_title', 'Historial de pagos')}</h3>
                <p>{st('aside.payments_body', 'Revisa cobros Stripe, renovaciones y movimientos anteriores.')}</p>
                <Link to="/business/transactions">
                  {st('actions.open_history', 'Abrir historial')}
                  <ArrowUpRight size={15} />
                </Link>
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionsManager;
